import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Avatar,
  Button
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Games as GamesIcon,
  Book as BookIcon,
  Headphones as HeadphonesIcon,
  List as ListIcon,
  School as SchoolIcon,
  EmojiEvents as TrophyIcon,
  Settings as SettingsIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import EducationGhostTutorial from './EducationGhostTutorial'
import LevelChoice from './LevelChoice'
import Reading from './Reading'
import Listening from './Listening'
import WordList from './WordList'
import GameSelection from './GameSelection'
import Leaderboard from './Leaderboard'
import Settings from './Settings'
import TeacherDashboard from './TeacherDashboard'
import Dashboard from './Dashboard'
import SpaceTheme from '../components/SpaceTheme'

const Education = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [userLevel, setUserLevel] = useState(null)
  const [currentPage, setCurrentPage] = useState('education')

  const [showGhostTutorial, setShowGhostTutorial] = useState(false)
  const [highlightedCard, setHighlightedCard] = useState(null)
  
  // userData prop'unun undefined olma durumunu handle et
  const safeUserData = userData || {}

  useEffect(() => {
    if (user && user.uid) {
      loadUserLevel()
    } else {
      setLoading(false)
    }
  }, [user, userData])

  const loadUserLevel = async () => {
    try {
      setLoading(true)
      
      if (safeUserData?.level) {
        setUserLevel(safeUserData.level)
      } else if (user?.uid) {
        // Firestore'dan kullanıcı seviyesini al
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        if (userDoc.exists()) {
          const level = userDoc.data().level
          setUserLevel(level)
        }
      }
    } catch (error) {
      console.error('Error loading user level:', error)
      toast.error('Kullanıcı seviyesi yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleGameClick = () => {
    if (!userLevel) {
      toast.error('Önce seviye seçmelisiniz')
      return
    }
    setCurrentPage('game-selection')
  }

  const handleReadingClick = () => {
    if (!userLevel) {
      toast.error('Önce seviye seçmelisiniz')
      return
    }
    setCurrentPage('reading')
  }

  const handleListeningClick = () => {
    if (!userLevel) {
      toast.error('Önce seviye seçmelisiniz')
      return
    }
    setCurrentPage('listening')
  }

  const handleWordListClick = () => {
    if (!userLevel) {
      toast.error('Önce seviye seçmelisiniz')
      return
    }
    setCurrentPage('word-list')
  }

  const educationItems = [
    {
      id: 'reading',
      title: 'Okuma',
      icon: <BookIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      color: 'primary',
      action: handleReadingClick
    },
    {
      id: 'game',
      title: 'Oyun',
      icon: <GamesIcon sx={{ fontSize: 32, color: 'secondary.main' }} />,
      color: 'secondary',
      action: handleGameClick
    },
    {
      id: 'listening',
      title: 'Dinleme',
      icon: <HeadphonesIcon sx={{ fontSize: 32, color: 'success.main' }} />,
      color: 'success',
      action: handleListeningClick
    },
    {
      id: 'word-list',
      title: 'Kelime Listesi',
      icon: <ListIcon sx={{ fontSize: 32, color: 'warning.main' }} />,
      color: 'warning',
      action: handleWordListClick
    }
  ]

  // Avatar için kullanıcı bilgilerini hazırla
  const getAvatarProps = () => {
    if (safeUserData?.profileImageUrl) {
      return { src: safeUserData.profileImageUrl, alt: safeUserData.username }
    }
    
    const displayName = safeUserData?.username || user?.displayName || user?.email?.split('@')[0] || 'U'
    return { 
      children: displayName.charAt(0).toUpperCase(),
      alt: displayName
    }
  }

  const handleMenuItemClick = (item) => {
    if (item.action) {
      item.action()
    }
  }

  const handleGhostTutorial = () => {
    setShowGhostTutorial(true)
    // İlk adımda hiçbir kartı vurgulama
    setHighlightedCard(null)
  }

  const handleCloseGhostTutorial = () => {
    setShowGhostTutorial(false)
    setHighlightedCard(null)
  }

  const handleHighlightCard = (cardId) => {
    setHighlightedCard(cardId)
  }

  const handleBackToEducation = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('education')
    }
  }



  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                console.log('Education back button clicked, onBack:', onBack)
                if (onBack && typeof onBack === 'function') {
                  onBack()
                } else {
                  console.error('onBack is not a function:', onBack)
                }
              }}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Eğitim
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" sx={{ 
          py: { xs: 1, sm: 2, md: 3 },
          px: { xs: 1, sm: 2 }
        }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  // Level Choice sayfasını göster
  if (currentPage === 'level-choice') {
    return <LevelChoice userData={userData} onBack={handleBackToEducation} />
  }

  // Reading sayfasını göster
  if (currentPage === 'reading') {
    return <Reading userData={userData} onBack={handleBackToEducation} />
  }

  // Listening sayfasını göster
  if (currentPage === 'listening') {
    return <Listening userData={userData} onBack={handleBackToEducation} />
  }

  // Word List sayfasını göster
  if (currentPage === 'word-list') {
    return <WordList userData={userData} onBack={handleBackToEducation} />
  }

  // Game Selection sayfasını göster
  if (currentPage === 'game-selection') {
    return <GameSelection 
      userData={userData} 
      userLevel={userLevel}
      onBack={handleBackToEducation} 
    />
  }

  // Leaderboard sayfasını göster
  if (currentPage === 'leaderboard') {
    return <Leaderboard userData={userData} onBack={handleBackToEducation} />
  }

  // Settings sayfasını göster
  if (currentPage === 'settings') {
    return <Settings userData={userData} onBack={handleBackToEducation} />
  }

  // Teacher Dashboard sayfasını göster
  if (currentPage === 'teacher-dashboard') {
    return <TeacherDashboard userData={userData} onBack={handleBackToEducation} />
  }

  // Dashboard sayfasını göster
  if (currentPage === 'dashboard') {
    return <Dashboard userData={userData} />
  }



  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              console.log('Education main back button clicked, onBack:', onBack)
              if (onBack && typeof onBack === 'function') {
                onBack()
              } else {
                console.error('onBack is not a function:', onBack)
              }
            }}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          {/* Logo */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'white', 
            borderRadius: '50%', 
            width: { xs: 40, sm: 56, md: 66 },
            height: { xs: 40, sm: 56, md: 66 },
            marginRight: { xs: 1, sm: 2 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <img 
              src="/logo.png" 
              alt="EngliX Logo" 
              style={{ 
                width: window.innerWidth < 600 ? 28 : window.innerWidth < 900 ? 40 : 50, 
                height: window.innerWidth < 600 ? 28 : window.innerWidth < 900 ? 40 : 50, 
                objectFit: 'contain'
              }}
              onError={(e) => {
                console.log('Logo yüklenemedi, alternatif yollar deneniyor...')
                // Alternatif logo yollarını dene
                if (e.target.src.includes('logo.png')) {
                  e.target.src = '/logo.jpg'
                } else if (e.target.src.includes('logo.jpg')) {
                  e.target.src = '/logo.svg'
                } else if (e.target.src.includes('logo.svg')) {
                  e.target.src = '/logo.webp'
                } else {
                  e.target.style.display = 'none'
                  console.log('Logo dosyası bulunamadı')
                }
              }}
            />
          </Box>
          
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 'bold',
            letterSpacing: '0.5px',
            fontSize: { xs: '1rem', sm: '1.5rem' }
          }}>
            Eğitim
          </Typography>
          
          {/* Navigation Buttons - Responsive */}
          {/* Dashboard Button - Her zaman görünür */}
          <IconButton
            color="inherit"
            onClick={() => {
              if (safeUserData?.role === 'teacher') {
                setCurrentPage('teacher-dashboard')
              } else {
                setCurrentPage('dashboard')
              }
            }}
            sx={{ mr: 1 }}
          >
            <SchoolIcon />
          </IconButton>
          
          {/* Leaderboard - Tablet+ */}
          <IconButton
            color="inherit"
            onClick={() => setCurrentPage('leaderboard')}
            sx={{ 
              mr: 1,
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            <TrophyIcon />
          </IconButton>
          
          {/* Settings - Her zaman görünür */}
          <IconButton
            color="inherit"
            onClick={() => setCurrentPage('settings')}
            sx={{ mr: 1 }}
          >
            <SettingsIcon />
          </IconButton>
          
          {userLevel && (
            <Chip 
              label={`Seviye: ${userLevel}`} 
              color="secondary"
              size="small"
              icon={<SchoolIcon />}
            />
          )}
        </Toolbar>
      </AppBar>

             {/* Main Content */}
       <Container maxWidth="sm" sx={{ 
         py: { xs: 1, sm: 2, md: 3 },
         px: { xs: 1, sm: 2 }
       }}>
                 {/* Header Card - Android uygulamasındaki gibi */}
         <Card sx={{ 
                     mx: { xs: 0.5, sm: 2 }, 
          mt: { xs: 1, sm: 2 }, 
           borderRadius: 3,
           boxShadow: 3,
           backgroundColor: '#fff'
         }}>
           <CardContent sx={{ p: 3 }}>
             <Box display="flex" alignItems="center" justifyContent="space-between">
               <Box display="flex" alignItems="center">
                 <Avatar
                   sx={{ 
                     width: 56, 
                     height: 56, 
                     mr: 2,
                     cursor: 'pointer',
                     border: '2px solid #e0e0e0'
                   }}
                   {...getAvatarProps()}
                 />
                 <Box>
                                       <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', fontSize: '22px' }}>
                      {safeUserData?.username || user?.displayName || (safeUserData?.role === 'teacher' ? 'Öğretmen' : 'Öğrenci') || 'Kullanıcı'}
                    </Typography>
                   <Box display="flex" alignItems="center">
                     <Typography variant="body1" sx={{ color: '#333', fontSize: '16px' }}>
                       Level: {userLevel || 'A1'}
                     </Typography>
                     <Button
                       size="small"
                       sx={{ 
                         ml: 1, 
                         textTransform: 'none',
                         color: '#666',
                         fontSize: '12px',
                         minWidth: 'auto',
                         p: 0.5
                       }}
                       onClick={() => setCurrentPage('level-choice')}
                     >
                       Değiştir
                     </Button>
                   </Box>
                 </Box>
               </Box>
               
                               {/* Ghost Help Button */}
                <IconButton
                  onClick={handleGhostTutorial}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#f0f0f0',
                    color: '#333',
                    '&:hover': {
                      backgroundColor: '#e0e0e0'
                    }
                  }}
                >
                  <Typography variant="h6">👻</Typography>
                </IconButton>
             </Box>
           </CardContent>
         </Card>

                 {/* Section Title */}
         <Typography variant="h4" sx={{ 
           px: { xs: 1, sm: 2 }, 
           mt: 4, 
           mb: 2, 
           color: '#333',
           fontWeight: 'bold',
           fontSize: '24px'
         }}>
           Dersleriniz
         </Typography>

         {/* Education Items */}
         <Box sx={{ px: 0, mt: 2 }}>
           <List sx={{ px: 0 }}>
                         {educationItems.map((item, index) => (
               <React.Fragment key={item.id}>
                                  <ListItem
                    button
                    onClick={() => handleMenuItemClick(item)}
                    sx={{
                      borderRadius: 3,
                      mb: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: highlightedCard === item.id ? '0 12px 40px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.2)',
                      minHeight: 80,
                      opacity: showGhostTutorial && highlightedCard !== item.id ? 0.2 : 1,
                      transform: highlightedCard === item.id ? 'scale(1.02)' : 'none',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateX(4px) scale(1.02)',
                        transition: 'all 0.3s ease-in-out',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                   <ListItemIcon>
                     {React.cloneElement(item.icon, { sx: { fontSize: 40, ...item.icon.props.sx } })}
                   </ListItemIcon>
                   <ListItemText
                     primary={item.title}
                     primaryTypographyProps={{
                       variant: 'h5',
                       fontWeight: 'bold',
                       color: '#333',
                       fontSize: '20px'
                     }}
                   />
                 </ListItem>
                
              </React.Fragment>
            ))}
                     </List>
         </Box>

         {/* Info Alert */}
         {!userLevel && (
           <Card sx={{ mt: 3, mx: { xs: 0.5, sm: 2 } }}>
             <CardContent sx={{ textAlign: 'center', py: 3 }}>
               <SchoolIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
               <Typography variant="h6" color="warning.main" gutterBottom>
                 Seviye Seçilmemiş
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 Eğitim materyallerini kullanabilmek için önce seviye seçmelisiniz.
               </Typography>
             </CardContent>
           </Card>
         )}
       </Container>

       {/* Bottom Navigation - REMOVED */}

      {/* Ghost Tutorial Overlay */}
      {showGhostTutorial && (
        <EducationGhostTutorial
          onBack={onBack}
          onHighlightCard={handleHighlightCard}
          onClose={handleCloseGhostTutorial}
        />
      )}
    </SpaceTheme>
  )
}

export default Education
